import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import {
  normalizeCatalogSearchFilters,
  type NormalizedCatalogSearchFilters
} from '@/features/catalog';
import { buildUrlFromFilters } from './filters';
import type { UsePuppySearchResult } from './usePuppySearch';

export type UsePuppySearchNavigationOptions = {
  search: Pick<UsePuppySearchResult, 'filters'>;
};

export function usePuppySearchNavigation(
  options: UsePuppySearchNavigationOptions
) {
  const { search } = options;
  const router = useRouter();

  const applyAndNavigate = useCallback(
    (partial: Partial<Omit<NormalizedCatalogSearchFilters, 'page'>>) => {
      // Build new filters by merging partial with current filters.
      // Do NOT fetch data client-side; let SSR handle the fetch on navigation.
      // This avoids double-fetching and race conditions with SSR data.
      const nextFilters = normalizeCatalogSearchFilters({
        ...search.filters,
        ...partial,
        page: 1
      });
      const { pathname, search: queryString } =
        buildUrlFromFilters(nextFilters);
      const href =
        queryString.length > 0 ? `${pathname}?${queryString}` : pathname;

      if (nextFilters.breedSlug) {
        // Navigate to canonical breed URL (e.g., /puppies/goldendoodle/ontario)
        router.push(href, { scroll: false });
      } else {
        // Stay on /puppies with query params
        router.replace(href, { scroll: false });
      }

      return nextFilters;
    },
    [router, search.filters]
  );

  return { applyAndNavigate };
}
